import { upgradeInfo } from './upgradeRequired.mjs';

/**
 * Normalize Axios error responses into user-facing strings.
 *
 * Reusable in every auth/registration screen. The three most common
 * shapes we have to handle:
 *  - 402 upgrade required         → name the feature and who can unlock it
 *  - 429 with Retry-After header  → tell the user the wait time
 *  - 4xx with {message, errors}   → surface the server message
 *  - network error (no response)  → generic "check your connection"
 *
 * Returns an object with:
 *   message     - string to show in the snackbar
 *   isThrottled - true for HTTP 429 so the caller can start a local
 *                  button cooldown instead of letting the user hammer
 *                  it and compound the throttle
 *   isUpgradeRequired - true for HTTP 402 so the caller can offer an upgrade
 *                  instead of a "try again" that can never succeed
 *   feature     - which gate refused, when the server named one
 *   retryAfterSec - parsed Retry-After in seconds, 0 if unknown
 *   status      - the raw HTTP status if present
 */
export function apiErrorMessage(errorResponse, fallback = "Something went wrong. Please try again.", options) {
    // `= {}` in the signature only defaults on undefined, so an explicit null -
    // easy to pass through from a caller that builds its options object - would
    // throw here instead of falling back.
    const { canUpgrade = true } = options ?? {};
    const status = errorResponse?.response?.status;
    const headers = errorResponse?.response?.headers ?? {};
    const serverMessage = errorResponse?.response?.data?.message;

    // Before the generic branches: a 402 carries a server message, but it is the
    // same sentence for every gate, so passing it through verbatim would lose
    // which feature was refused and would tell a member to do something only the
    // circle owner can.
    const upgrade = upgradeInfo(errorResponse, { canUpgrade });
    if (upgrade) {
        return {
            message: upgrade.message,
            isThrottled: false,
            isUpgradeRequired: true,
            feature: upgrade.feature,
            canUpgrade: upgrade.canUpgrade,
            retryAfterSec: 0,
            status,
        };
    }

    if (status === 429) {
        const retryAfterSec = Number(
            headers['retry-after'] ?? headers['Retry-After'] ?? 0,
        );
        const minutes = retryAfterSec > 0 ? Math.ceil(retryAfterSec / 60) : null;
        const message = minutes
            ? `Too many attempts. Try again in about ${minutes} minute${minutes > 1 ? 's' : ''}.`
            : "Too many attempts. Please wait a few minutes and try again.";
        return {
            message,
            isThrottled: true,
            isUpgradeRequired: false,
            feature: null,
            canUpgrade,
            retryAfterSec,
            status,
        };
    }

    // No response object = network error (CORS, offline, DNS, etc.)
    if (!errorResponse?.response) {
        return {
            message: "Can't reach the server. Check your connection and try again.",
            isThrottled: false,
            isUpgradeRequired: false,
            feature: null,
            canUpgrade,
            retryAfterSec: 0,
            status: null,
        };
    }

    // Server returned an explicit message → surface it verbatim (Laravel
    // validation errors put the human-readable text here). Blank counts as
    // absent: `??` alone would turn `message: ""` into an empty snackbar, where
    // the call sites this replaced used `||` and showed their own fallback.
    const usableMessage =
        typeof serverMessage === "string" && serverMessage.trim() !== "" ? serverMessage : null;

    return {
        message: usableMessage ?? fallback,
        isThrottled: false,
        isUpgradeRequired: false,
        feature: null,
        canUpgrade,
        retryAfterSec: 0,
        status,
    };
}

/**
 * Convenience wrapper that routes the normalized message to a snackbar
 * and returns the parsed info in case the caller needs the cooldown
 * number.
 */
export function showApiError(snackbar, errorResponse, fallback, options) {
    const info = apiErrorMessage(errorResponse, fallback, options);
    snackbar.showSnackbar(info.message, 'error');
    return info;
}
